import * as CL from '@design-system/component-library';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { removeEmployeeProductList } from '../../common/fetch.js';
import {
  alvZeroMsg,
  billingAccountSelectedPerOrderMsg,
  catalogCorporateMessageLabelMsg,
  companysShareOfMonthlyFeeMsg,
  contractPeriodMsg,
  damageInsuranceMsg,
  defaultBillingAccountMsg,
  deviceEnrollmentProgramMsg,
  lastModifiedMsg,
  productTypeMsg,
  removeDeviceListMsg,
  restrictedBillingAccountsMsg,
  somethingWentWrongMsg,
  statusMsg,
  t,
} from '../../common/i18n/index.js';
import { getBillingAccountLabelCoCo } from '../../common/utils/billingAccountUtilsCoCo.js';
import { formatDateStringToDDMMYYYYHHmm } from '../../common/utils/dateUtilsCoCo.js';
import {
  getContractTermLabel,
  getSelectedContractTerms,
  toDeviceListBillingSettings,
} from '../../common/utils/deviceListFormUtilsCoCo.js';
import {
  getDeviceListsTabPath,
  getDeviceSelectionProductTypeLabel,
  getEditDeviceListPath,
} from '../../common/utils/deviceListTabsUtilsCoCo.js';
import {
  EMPTY_VALUE,
  getDamageInsuranceText,
  getDeviceListStatusColor,
  getDeviceListStatusLabel,
  getInUseText,
} from '../../common/utils/deviceListUtilsCoCo.js';
import { toDeviceSelectionProductType } from '../../common/utils/deviceSelectionListUtilsCoCo.js';
import { formatMonthlyPriceCoCo } from '../../common/utils/priceUtilsCoCo.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { ConfirmationDialog } from '../ConfirmationDialog/index.js';
import { DeviceSelectionDevicesCoCo } from '../DeviceSelectionDetailsCoCo/DeviceSelectionDevicesCoCo.js';
import { Status } from '../Status/Status.js';
import { CopyDeviceListDialogCoCo } from './CopyDeviceListDialogCoCo.js';
import type { DeviceListDetailsLoaderDataCoCo } from '../../common/loaders.js';
import type { BillingAccountCoco } from '../../generated/api/billingAccountCoco.js';
import type { EmployeeProductListResponse } from '../../generated/api/employeeProductListResponse.js';
import type { ReactNode } from 'react';

import '../DeviceSelectionDetailsCoCo/DeviceSelectionDetailsCoCo.scss';
import './DeviceListDetailsCoCo.scss';

const getCompanyShareText = ({ productType, companyShare = {} }: EmployeeProductListResponse) => {
  const contractTerms = getSelectedContractTerms(productType, Object.keys(companyShare));
  return contractTerms.length > 0
    ? contractTerms.map(contractTerm => (
        <div key={contractTerm}>
          {`${getContractTermLabel(contractTerm)}: ${formatMonthlyPriceCoCo(companyShare[contractTerm])}`}
        </div>
      ))
    : EMPTY_VALUE;
};

const getBillingAccountText = (billingAccounts: BillingAccountCoco[], billingAccountId?: string) => {
  if (!billingAccountId) {
    return EMPTY_VALUE;
  }
  const billingAccount = billingAccounts.find(({ id }) => id === billingAccountId);
  // Falls back to the id, if the billing account is not among the billing accounts of the company
  return billingAccount ? getBillingAccountLabelCoCo(billingAccount) : billingAccountId;
};

// The items are shown in two columns, ordered row by row from left to right
const DetailsGrid = ({ items }: { items: { title: string; description: ReactNode }[] }) => (
  <dl className="of-device-list-details-coco__details">
    {items.map(({ title, description }) => (
      <div key={title} className="of-device-list-details-coco__detail">
        <dt className={dsClass.FONT_WEIGHT_BOLD}>{title}</dt>
        <dd>{description}</dd>
      </div>
    ))}
  </dl>
);

export type DeviceListDetailsCoCoProps = DeviceListDetailsLoaderDataCoCo;

export const DeviceListDetailsCoCo = ({
  deviceList,
  billingAccounts,
  deviceSelection,
  deviceVariantGroups,
}: DeviceListDetailsCoCoProps) => {
  const billing = toDeviceListBillingSettings(deviceList);
  const contractTerms = getSelectedContractTerms(deviceList.productType, deviceList.contractTerms ?? []);
  const navigate = useNavigate();
  const [showRemoveDeviceListDialog, setShowRemoveDeviceListDialog] = useState(false);
  const [showCopyDeviceListDialog, setShowCopyDeviceListDialog] = useState(false);
  const [removingDeviceList, setRemovingDeviceList] = useState(false);
  const removeDeviceListRef = useRef(false);

  const closeRemoveDeviceListDialog = () => {
    if (!removeDeviceListRef.current) {
      setShowRemoveDeviceListDialog(false);
    }
  };

  const handleRemoveDeviceList = async () => {
    if (removeDeviceListRef.current || !deviceList.uuid) {
      return;
    }
    removeDeviceListRef.current = true;
    setRemovingDeviceList(true);
    try {
      const response = await removeEmployeeProductList(deviceList.uuid, deviceList.accountMdmId);
      if (!response.ok) {
        throw new Error('Failed to delete device list');
      }
      setShowRemoveDeviceListDialog(false);
      navigate(deviceList.accountMdmId ? getDeviceListsTabPath(deviceList.accountMdmId) : paths.COMPANY_INFO_CATALOGS);
    } catch {
      addNotification(t.JDTO(somethingWentWrongMsg), 'error');
    } finally {
      removeDeviceListRef.current = false;
      setRemovingDeviceList(false);
    }
  };

  return (
    <div className="of-device-list-details-coco__content">
      <DetailsGrid
        items={[
          {
            title: t.ASQT(statusMsg),
            description: deviceList.status ? (
              <Status
                color={getDeviceListStatusColor(deviceList.status)}
                textAfterBadge={getDeviceListStatusLabel(deviceList.status)}
              />
            ) : (
              EMPTY_VALUE
            ),
          },
          {
            title: t.D55M(contractPeriodMsg),
            description: contractTerms.length > 0 ? contractTerms.map(getContractTermLabel).join(', ') : EMPTY_VALUE,
          },
          {
            title: t.PM1H(lastModifiedMsg),
            description: formatDateStringToDDMMYYYYHHmm(deviceList.lastModifiedDate) ?? EMPTY_VALUE,
          },
          { title: t.QSXP(damageInsuranceMsg), description: getDamageInsuranceText(deviceList.damageInsuranceOptions) },
          {
            title: t.EYPA(defaultBillingAccountMsg),
            description: getBillingAccountText(billingAccounts, billing.billingAccountId),
          },
          { title: t.Q03S(deviceEnrollmentProgramMsg), description: getInUseText(deviceList.enrollmentProgram) },
          {
            title: `${t.H8Q4(companysShareOfMonthlyFeeMsg)} (${t.S8TX(alvZeroMsg)})`,
            description: getCompanyShareText(deviceList),
          },
          {
            title: t.RXBO(billingAccountSelectedPerOrderMsg),
            description: getInUseText(billing.approverMustSelectBillingAccount),
          },
          {
            title: t.R6HP(productTypeMsg),
            description: getDeviceSelectionProductTypeLabel(toDeviceSelectionProductType(deviceList.productType)),
          },
          {
            title: t.RP0S(restrictedBillingAccountsMsg),
            description: getInUseText(Boolean(billing.selectedBillingAccountIds?.length)),
          },
        ]}
      />
      <hr className={dsClass.MARGIN_VERTICAL_3} />
      <div className="of-device-selection-details-coco__actions">
        <CL.Button color="link" size="l" onClick={() => setShowRemoveDeviceListDialog(true)}>
          <CL.Icon icon="delete" />
          {t.XRLY(removeDeviceListMsg)}
        </CL.Button>
        <CL.Button
          color="link"
          size="l"
          onClick={() => deviceList.uuid && navigate(getEditDeviceListPath(deviceList.uuid, deviceList.accountMdmId))}
        >
          <CL.Icon icon="edit" />
          {t.NC59('Edit device list')}
        </CL.Button>
        <CL.Button color="link" size="l" onClick={() => setShowCopyDeviceListDialog(true)}>
          <CL.Icon icon="draft" />
          {t.OIG7('Copy device list')}
        </CL.Button>
      </div>
      {deviceList.customerAdminMessage && (
        <section className="of-device-list-details-coco__message">
          <h3 className={dsClass.H3}>{t.C8DA(catalogCorporateMessageLabelMsg)}</h3>
          <p>{deviceList.customerAdminMessage}</p>
        </section>
      )}
      {deviceSelection && deviceVariantGroups && (
        <DeviceSelectionDevicesCoCo deviceSelection={deviceSelection} deviceVariantGroups={deviceVariantGroups} />
      )}
      {showRemoveDeviceListDialog && (
        <ConfirmationDialog
          header={t.NBTN('Confirm deletion of device list')}
          body={deviceList.name ?? ''}
          onCloseDialog={closeRemoveDeviceListDialog}
          onConfirm={handleRemoveDeviceList}
          disableConfirm={removingDeviceList}
          isLoading={removingDeviceList}
          hideClosingCross={removingDeviceList}
        />
      )}
      {showCopyDeviceListDialog && (
        <CopyDeviceListDialogCoCo
          deviceList={deviceList}
          deviceSelection={deviceSelection}
          onClose={() => setShowCopyDeviceListDialog(false)}
        />
      )}
    </div>
  );
};
